/**
 * CardSelect — 카드 그리드 기반 단일 선택 컴포넌트.
 * Select.ts와 동일한 생명주기(init/getValue/setValue/destroy).
 * native <select>를 숨기고 카드 UI로 대체. form 제출 시 native 값 전송.
 *
 * <option> data 속성:
 *   data-icon  — Font Awesome 클래스 (e.g. "fa-light fa-text")
 *   data-desc  — 카드 하단 설명 텍스트
 */

const CHEVRON_SVG = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 4.5L6 7.5L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function mk<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

export interface CardSelectOptions {
  columns?: number;
  placeholder?: string;
  onChange?: (value: string, label: string) => void;
}

interface CardItem {
  value: string;
  label: string;
  icon: string;
  desc: string;
  disabled: boolean;
}

let _uid = 0;

export class CardSelect {
  private readonly el: HTMLSelectElement;
  private readonly opts: CardSelectOptions;
  private container!: HTMLDivElement;
  private trigger!: HTMLButtonElement;
  private triggerIcon!: HTMLSpanElement;
  private triggerLabel!: HTMLSpanElement;
  private panel!: HTMLDivElement;
  private gridEl!: HTMLUListElement;
  private allItems: CardItem[] = [];
  private isOpen = false;
  private readonly uid: string;

  constructor(target: string | HTMLSelectElement, opts: CardSelectOptions = {}) {
    const el = typeof target === "string"
      ? document.querySelector<HTMLSelectElement>(target)
      : target;
    if (!el) throw new Error("CardSelect: element not found");
    this.el = el;
    this.opts = opts;
    this.uid = `csg${++_uid}`;
  }

  init(): this {
    if (this.el.dataset.csgInited) return this;
    this._parse();
    this._build();
    this._wire();
    this.el.dataset.csgInited = "1";
    return this;
  }

  getValue(): string { return this.el.value; }

  setValue(v: string): void {
    if (!Array.from(this.el.options).find(o => o.value === v)) return;
    this.el.value = v;
    this._updateTrigger();
    this._refreshGrid();
    this.el.dispatchEvent(new Event("change", { bubbles: true }));
  }

  destroy(): void {
    document.removeEventListener("click", this._onDocClick, true);
    this.container?.remove();
    this.el.style.display = "";
    delete this.el.dataset.csgInited;
  }

  private _parse(): void {
    this.allItems = Array.from(this.el.options)
      .filter(o => !(o.value === "" && o.text.trim() === ""))
      .map(o => ({
        value: o.value,
        label: o.text.trim(),
        icon: o.dataset.icon ?? "",
        desc: o.dataset.desc ?? "",
        disabled: o.disabled,
      }));
  }

  private _build(): void {
    this.el.style.display = "none";

    this.container = mk("div", { class: "csg-wrap" });

    // Trigger
    this.trigger = mk("button", {
      type: "button",
      class: "csg-trigger",
      "aria-haspopup": "listbox",
      "aria-expanded": "false",
      "aria-controls": `${this.uid}-panel`,
    }) as HTMLButtonElement;
    this.triggerIcon = mk("span", { class: "csg-trigger__icon", "aria-hidden": "true" });
    this.triggerLabel = mk("span", { class: "csg-trigger__label" });
    const arrow = mk("span", { class: "csg-trigger__arrow", "aria-hidden": "true" });
    arrow.innerHTML = CHEVRON_SVG;
    this.trigger.append(this.triggerIcon, this.triggerLabel, arrow);
    this._updateTrigger();

    // Panel
    this.panel = mk("div", {
      class: "csg-panel",
      id: `${this.uid}-panel`,
      role: "listbox",
    }) as HTMLDivElement;
    this.panel.hidden = true;

    const cols = this.opts.columns ?? 4;
    this.gridEl = mk("ul", {
      class: "csg-grid",
      style: `--csg-cols:${cols}`,
    }) as HTMLUListElement;
    this._buildGrid();
    this.panel.appendChild(this.gridEl);

    this.container.append(this.trigger, this.panel);
    this.el.insertAdjacentElement("afterend", this.container);
  }

  private _buildGrid(): void {
    this.gridEl.innerHTML = "";
    const cur = this.el.value;
    for (const item of this.allItems) {
      const sel = item.value === cur;
      const cls = ["csg-card", sel && "csg-card--selected", item.disabled && "csg-card--disabled"]
        .filter(Boolean).join(" ");
      const li = mk("li", {
        class: cls,
        role: "option",
        "data-value": item.value,
        "aria-selected": String(sel),
        ...(item.disabled ? { "aria-disabled": "true" } : {}),
      });

      if (item.icon) {
        const iconWrap = mk("span", { class: "csg-card__icon", "aria-hidden": "true" });
        const i = mk("i", { class: item.icon });
        iconWrap.appendChild(i);
        li.appendChild(iconWrap);
      }

      const labelEl = mk("span", { class: "csg-card__label" });
      labelEl.textContent = item.label;
      li.appendChild(labelEl);

      if (item.desc) {
        const descEl = mk("span", { class: "csg-card__desc" });
        descEl.textContent = item.desc;
        li.appendChild(descEl);
      }

      this.gridEl.appendChild(li);
    }
  }

  private _refreshGrid(): void {
    this._buildGrid();
  }

  private _updateTrigger(): void {
    const sel = Array.from(this.el.options).find(o => o.selected && o.value !== "");
    this.triggerIcon.innerHTML = "";
    if (sel) {
      const icon = sel.dataset.icon ?? "";
      if (icon) {
        const i = mk("i", { class: icon });
        this.triggerIcon.appendChild(i);
      }
      this.triggerLabel.textContent = sel.text.trim();
      this.triggerLabel.classList.remove("csg-trigger__label--empty");
    } else {
      this.triggerLabel.textContent = this.opts.placeholder ?? "선택하세요";
      this.triggerLabel.classList.add("csg-trigger__label--empty");
    }
  }

  private _pick(value: string): void {
    const item = this.allItems.find(i => i.value === value);
    if (!item || item.disabled) return;
    this.el.value = value;
    this._updateTrigger();
    this._refreshGrid();
    this._close();
    this.el.dispatchEvent(new Event("change", { bubbles: true }));
    this.opts.onChange?.(value, item.label);
  }

  private _open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this._position();
    this.panel.hidden = false;
    this.trigger.setAttribute("aria-expanded", "true");
    this.trigger.classList.add("csg-trigger--open");
  }

  private _close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.panel.hidden = true;
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.classList.remove("csg-trigger--open");
  }

  private _position(): void {
    const r = this.container.getBoundingClientRect();
    const goUp = window.innerHeight - r.bottom < 280 && r.top > window.innerHeight - r.bottom;
    this.panel.classList.toggle("csg-panel--up", goUp);
  }

  private _wire(): void {
    this.trigger.addEventListener("click", () => this.isOpen ? this._close() : this._open());
    this.trigger.addEventListener("keydown", (e) => {
      if (e.key === "Escape") { this._close(); this.trigger.focus(); }
      else if ([" ", "Enter", "ArrowDown"].includes(e.key)) { e.preventDefault(); this._open(); }
    });
    this.gridEl.addEventListener("click", (e) => {
      const li = (e.target as Element).closest<HTMLElement>(".csg-card");
      if (!li || li.classList.contains("csg-card--disabled")) return;
      this._pick(li.dataset.value ?? "");
    });
    this.gridEl.addEventListener("keydown", (e) => {
      if (e.key === "Escape") { this._close(); this.trigger.focus(); }
    });
    document.addEventListener("click", this._onDocClick, true);
    this.el.addEventListener("change", () => { this._updateTrigger(); this._refreshGrid(); });
  }

  private _onDocClick = (e: MouseEvent): void => {
    if (!this.container.contains(e.target as Node)) this._close();
  };
}
